import React, { useEffect, useState, Children, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Battery, Brain, Activity, CheckCircle2, XCircle, BookOpen, Calendar, ShieldCheck, ArrowRight, Coffee, AlertCircle, X, Zap, Download, Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}
// Initialize Meta Pixel
function initMetaPixel() {
  if (typeof window !== 'undefined' && !window.fbq) {
    const n: any = window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    // Load the Facebook Pixel script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    // Initialize with your Pixel ID
    window.fbq('init', '985509097980778');
  }
}
// Track PageView
function trackPageView() {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'PageView');
  }
}
// Track AddToCart
function trackAddToCart() {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: 'Perimenopausia Sin Caos',
      content_type: 'product',
      value: 6.36,
      currency: 'USD'
    });
  }
}
// Testimonials data
const testimonials = [{
  id: 1,
  name: 'María Elena',
  age: 52,
  location: 'Ciudad de México, México',
  image: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=150&h=150&fit=crop&crop=face',
  rating: 5,
  quote: 'Llevaba 2 años sintiéndome como extraña en mi propio cuerpo. Esta guía me dio claridad en una semana. Ahora sé exactamente qué hacer cuando me despierto a las 3am.',
  highlight: 'claridad en una semana'
}, {
  id: 2,
  name: 'Carmen Lucía',
  age: 48,
  location: 'Buenos Aires, Argentina',
  image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face',
  rating: 5,
  quote: 'Por fin algo en español que no me hace sentir que estoy loca. El mapa de síntomas cambió todo. Mi hinchazón bajó notablemente en 10 días.',
  highlight: 'hinchazón bajó en 10 días'
}, {
  id: 3,
  name: 'Rosa Martínez',
  age: 55,
  location: 'Miami, Florida',
  image: 'https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=150&h=150&fit=crop&crop=face',
  rating: 5,
  quote: 'Gasté cientos de dólares en suplementos sin saber qué hacía. Esta guía me enseñó a escuchar mi cuerpo primero. Simple pero poderoso.',
  highlight: 'escuchar mi cuerpo primero'
}, {
  id: 4,
  name: 'Patricia Gómez',
  age: 51,
  location: 'Bogotá, Colombia',
  image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face',
  rating: 5,
  quote: 'Los micro-hábitos son geniales porque no requieren fuerza de voluntad. Empecé con uno solo y ahora tengo 4 que hago sin pensar.',
  highlight: 'no requieren fuerza de voluntad'
}, {
  id: 5,
  name: 'Ana Sofía',
  age: 49,
  location: 'Madrid, España',
  image: 'https://images.unsplash.com/photo-1598550874175-4d0ef436c909?w=150&h=150&fit=crop&crop=face',
  rating: 5,
  quote: 'Mi médico me dijo que todo era "normal para tu edad". Esta guía me dio las preguntas correctas para mi próxima cita. Me sentí empoderada.',
  highlight: 'preguntas correctas para mi médico'
}, {
  id: 6,
  name: 'Lucía Fernández',
  age: 54,
  location: 'Santiago, Chile',
  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
  rating: 5,
  quote: 'El protocolo de sueño fue lo primero que probé. Después de años despertándome agotada, ahora duermo 6 horas seguidas. Es un milagro.',
  highlight: '6 horas seguidas de sueño'
}];
// Testimonials Carousel Component
function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const nextTestimonial = () => {
    setDirection(1);
    setActiveIndex(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setDirection(-1);
    setActiveIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  // Auto-advance on mobile
  useEffect(() => {
    const timer = setInterval(() => {
      nextTestimonial();
    }, 6000);
    return () => clearInterval(timer);
  }, []);
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };
  return <section className="py-20 px-4 bg-[#5D3A2E] text-[#F5EDE6] overflow-hidden" data-id="element-1">
      <div className="max-w-6xl mx-auto" data-id="element-2">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="text-center mb-12" data-id="element-3">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-4">
            Lo que dicen mujeres como tú
          </h2>
          <p className="text-[#E8D4C9] text-lg" data-id="element-5">
            Historias reales de mujeres que encontraron su mapa
          </p>
        </motion.div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative" data-id="element-6">
          <div className="overflow-hidden" data-id="element-7">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-8">
              <motion.div key={activeIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} className="w-full" data-id="element-9">
                <TestimonialCard testimonial={testimonials[activeIndex]} data-id="element-10" />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6" data-id="element-11">
            <button onClick={prevTestimonial} className="p-2 rounded-full bg-[#C4705A]/20 hover:bg-[#C4705A]/40 transition-colors" aria-label="Anterior testimonio" data-id="element-12">
              <ChevronLeft className="w-5 h-5" data-id="element-13" />
            </button>
            <div className="flex gap-2" data-id="element-14">
              {testimonials.map((_, i) => <button key={i} onClick={() => {
              setDirection(i > activeIndex ? 1 : -1);
              setActiveIndex(i);
            }} className={`w-2 h-2 rounded-full transition-all ${i === activeIndex ? 'bg-[#C4705A] w-6' : 'bg-[#E8D4C9]/30'}`} aria-label={`Ir al testimonio ${i + 1}`} data-id="element-15" />)}
            </div>
            <button onClick={nextTestimonial} className="p-2 rounded-full bg-[#C4705A]/20 hover:bg-[#C4705A]/40 transition-colors" aria-label="Siguiente testimonio" data-id="element-16">
              <ChevronRight className="w-5 h-5" data-id="element-17" />
            </button>
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-18">
          {testimonials.map((testimonial, index) => <motion.div key={testimonial.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} data-id="element-19">
              <TestimonialCard testimonial={testimonial} data-id="element-20" />
            </motion.div>)}
        </div>

        {/* Trust indicator */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mt-12 text-center" data-id="element-21">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#C4705A]/20 rounded-full" data-id="element-22">
            <div className="flex" data-id="element-23">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#C4705A] text-[#C4705A]" data-id="element-24" />)}
            </div>
            <span className="text-sm font-medium" data-id="element-25">
              +500 mujeres han transformado su día a día
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}
// Individual Testimonial Card
function TestimonialCard({
  testimonial
}: {
  testimonial: (typeof testimonials)[0];
}) {
  return <div className="bg-[#F5EDE6] text-[#5D3A2E] p-6 rounded-2xl h-full flex flex-col" data-id="element-26">
      {/* Header with photo and info */}
      <div className="flex items-center gap-4 mb-4" data-id="element-27">
        <div className="relative" data-id="element-28">
          <img src={testimonial.image} alt={testimonial.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#C4705A]" data-id="element-29" />
          <div className="absolute -bottom-1 -right-1 bg-[#C4705A] rounded-full p-0.5" data-id="element-30">
            <CheckCircle2 className="w-4 h-4 text-white" data-id="element-31" />
          </div>
        </div>
        <div data-id="element-32">
          <h4 className="font-bold text-[#5D3A2E]" data-id="element-33">{testimonial.name}</h4>
          <p className="text-sm text-[#5D3A2E]/60" data-id="element-34">{testimonial.location}</p>
        </div>
      </div>

      {/* Rating */}
      <div className="flex gap-0.5 mb-3" data-id="element-35">
        {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#C4705A] text-[#C4705A]" data-id="element-36" />)}
      </div>

      {/* Quote */}
      <div className="flex-1" data-id="element-37">
        <Quote className="w-6 h-6 text-[#C4705A]/30 mb-2" data-id="element-38" />
        <p className="text-[#5D3A2E]/80 leading-relaxed" data-id="element-39">
          {testimonial.quote.split(testimonial.highlight).map((part, i, arr) => <span key={i} data-id="element-40">
                {part}
                {i < arr.length - 1 && <span className="font-semibold text-[#C4705A]" data-id="element-41">
                    {testimonial.highlight}
                  </span>}
              </span>)}
        </p>
      </div>

      {/* Verified badge */}
      <div className="mt-4 pt-4 border-t border-[#5D3A2E]/10" data-id="element-42">
        <span className="text-xs text-[#5D3A2E]/50 flex items-center gap-1" data-id="element-43">
          <CheckCircle2 className="w-3 h-3" data-id="element-44" />
          Compra verificada
        </span>
      </div>
    </div>;
}
// Fake sales data for notifications
const salesData = [{
  name: 'María',
  city: 'Miami'
}, {
  name: 'Carmen',
  city: 'Houston'
}, {
  name: 'Ana',
  city: 'Los Angeles'
}, {
  name: 'Rosa',
  city: 'Chicago'
}, {
  name: 'Elena',
  city: 'Phoenix'
}, {
  name: 'Patricia',
  city: 'San Antonio'
}, {
  name: 'Laura',
  city: 'Dallas'
}, {
  name: 'Sofía',
  city: 'New York'
}, {
  name: 'Isabel',
  city: 'Denver'
}, {
  name: 'Lucía',
  city: 'San Diego'
}, {
  name: 'Gabriela',
  city: 'Austin'
}, {
  name: 'Teresa',
  city: 'Orlando'
}];
// Mobile Sales Notification Component
function MobileSalesNotification() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSale, setCurrentSale] = useState(0);
  useEffect(() => {
    // Initial delay before first notification
    const initialDelay = setTimeout(() => {
      setIsVisible(true);
    }, 3000);
    return () => clearTimeout(initialDelay);
  }, []);
  useEffect(() => {
    if (!isVisible) return;
    // Hide after 4 seconds
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, 4000);
    return () => clearTimeout(hideTimer);
  }, [isVisible, currentSale]);
  useEffect(() => {
    if (isVisible) return;
    // Show next notification after 5 seconds
    const showTimer = setTimeout(() => {
      setCurrentSale(prev => (prev + 1) % salesData.length);
      setIsVisible(true);
    }, 5000);
    return () => clearTimeout(showTimer);
  }, [isVisible]);
  const sale = salesData[currentSale];
  return <div className="md:hidden fixed bottom-6 left-4 right-4 z-50 pointer-events-none" data-id="element-45">
      <AnimatePresence data-id="element-46">
        {isVisible && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          type: 'spring',
          stiffness: 400,
          damping: 25
        }
      }} exit={{
        opacity: 0,
        y: 20,
        transition: {
          duration: 0.3
        }
      }} className="mx-auto max-w-sm" data-id="element-47">
            {/* iOS-style notification */}
            <div className="flex items-center gap-3 p-3 rounded-2xl shadow-lg border border-white/20" style={{
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)'
        }} data-id="element-48">
              {/* Product Image */}
              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-[#F5EDE6] shadow-sm" data-id="element-49">
                <img src="https://cdn.magicpatterns.com/uploads/rCFsGd68BdfSWJFkcCs28i/mockup_-_PERI.png" alt="Producto" className="w-full h-full object-cover" data-id="element-50" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-51">
                <p className="text-xs text-gray-500 font-medium" style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
            }} data-id="element-52">
                  Hace un momento
                </p>
                <p className="text-sm text-gray-900 leading-snug mt-0.5" style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
            }} data-id="element-53">
                  <span className="font-semibold" data-id="element-54">{sale.name}</span> de{' '}
                  {sale.city} compró{' '}
                  <span className="italic" data-id="element-55">Perimenopausia Sin Caos</span> por{' '}
                  <span className="font-bold text-[#C4705A]" data-id="element-56">$6.36</span>
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
// Modal component for Privacy and Terms
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-57">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50" onClick={onClose} data-id="element-58" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl md:w-full md:max-h-[80vh] bg-[#F5EDE6] rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col" data-id="element-59">
            <div className="flex items-center justify-between p-6 border-b border-[#5D3A2E]/10" data-id="element-60">
              <h3 className="text-2xl font-bold text-[#5D3A2E]" data-id="element-61">{title}</h3>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-[#5D3A2E]/10 transition-colors" data-id="element-62">
                <X className="w-6 h-6 text-[#5D3A2E]" data-id="element-63" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 text-[#5D3A2E]/80 space-y-4" data-id="element-64">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function SalesPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // Initialize Meta Pixel and track PageView on mount
  useEffect(() => {
    initMetaPixel();
    // Small delay to ensure pixel is loaded
    const timer = setTimeout(() => {
      trackPageView();
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  // Handle Add to Cart click
  const handleAddToCart = () => {
    trackAddToCart();
    // Navigate to Shopify cart
    window.open('https://instaflowdp.com/cart/63141465882993:1', '_blank');
  };
  return <div className="min-h-screen w-full bg-[#F5EDE6] text-[#5D3A2E] overflow-x-hidden" data-id="element-65">
      {/* Meta Pixel noscript fallback */}
      <noscript data-id="element-66">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=985509097980778&ev=PageView&noscript=1" alt="" data-id="element-67" />
      </noscript>

      {/* Mobile Sales Notification */}
      <MobileSalesNotification data-id="element-68" />

      {/* Privacy Modal */}
      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-69">
        <p className="font-semibold text-[#5D3A2E]" data-id="element-70">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          month: 'long',
          year: 'numeric'
        })}
        </p>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-71">
          1. Información que Recopilamos
        </h4>
        <p data-id="element-72">
          Recopilamos información personal que nos proporcionas directamente,
          como tu nombre y dirección de correo electrónico cuando realizas una
          compra o te suscribes a nuestra lista de correo.
        </p>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-73">
          2. Uso de la Información
        </h4>
        <p data-id="element-74">Utilizamos tu información para:</p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-75">
          <li data-id="element-76">Procesar y entregar tu compra</li>
          <li data-id="element-77">Enviarte actualizaciones sobre tu pedido</li>
          <li data-id="element-78">Comunicarnos contigo sobre productos y servicios</li>
        </ul>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-79">
          3. Protección de Datos
        </h4>
        <p data-id="element-80">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado,
          alteración, divulgación o destrucción.
        </p>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-81">4. Cookies</h4>
        <p data-id="element-82">
          Utilizamos cookies y tecnologías similares para mejorar tu experiencia
          en nuestro sitio web y analizar el tráfico.
        </p>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-83">5. Tus Derechos</h4>
        <p data-id="element-84">
          Tienes derecho a acceder, corregir o eliminar tu información personal.
          También puedes optar por no recibir comunicaciones de marketing en
          cualquier momento.
        </p>
      </LegalModal>

      {/* Terms Modal */}
      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-85">
        <p className="font-semibold text-[#5D3A2E]" data-id="element-86">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          month: 'long',
          year: 'numeric'
        })}
        </p>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-87">
          1. Aceptación de los Términos
        </h4>
        <p data-id="element-88">
          Al acceder y utilizar este sitio web y comprar nuestros productos,
          aceptas estar sujeto a estos términos y condiciones.
        </p>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-89">
          2. Productos Digitales
        </h4>
        <p data-id="element-90">
          Todos los productos vendidos son digitales y se entregan
          electrónicamente. Una vez completada la compra, recibirás acceso
          inmediato al contenido.
        </p>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-91">3. Uso del Contenido</h4>
        <p data-id="element-92">
          El contenido adquirido es para uso personal únicamente. No está
          permitido:
        </p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-93">
          <li data-id="element-94">Redistribuir o revender el contenido</li>
          <li data-id="element-95">Compartir el acceso con terceros</li>
          <li data-id="element-96">Modificar o crear obras derivadas</li>
        </ul>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-97">4. Política de Ventas</h4>
        <p data-id="element-98">
          Todas las ventas de productos digitales son finales. Debido a la
          naturaleza del contenido digital y su entrega inmediata, no se ofrecen
          reembolsos ni devoluciones.
        </p>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-99">
          5. Descargo de Responsabilidad
        </h4>
        <p data-id="element-100">
          Este producto es únicamente informativo y educativo. No sustituye el
          consejo médico profesional. Consulta siempre con un profesional de la
          salud antes de realizar cambios en tu dieta o estilo de vida.
        </p>

        <h4 className="font-bold text-[#5D3A2E] mt-6" data-id="element-101">
          6. Propiedad Intelectual
        </h4>
        <p data-id="element-102">
          Todo el contenido, incluyendo textos, gráficos, logos y materiales
          descargables, está protegido por derechos de autor y es propiedad
          exclusiva de Perimenopausia Sin Caos.
        </p>
      </LegalModal>

      {/* 1. HERO SECTION */}
      <header id="hero" className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 overflow-hidden" data-id="element-103">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#E8D4C9] rounded-bl-[100px] -z-10 opacity-50" data-id="element-104" />
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#C4705A] rounded-full mix-blend-multiply filter blur-3xl opacity-10" data-id="element-105" />

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-106">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-6 text-center md:text-left" data-id="element-107">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#C4705A]/10 text-[#C4705A] font-medium text-sm tracking-wide mx-auto md:mx-0" data-id="element-108">
              PARA MUJERES 45–60 AÑOS
            </span>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight text-[#5D3A2E]" data-id="element-109">
              No te falta disciplina. <br data-id="element-110" />
              <span className="text-[#C4705A] italic" data-id="element-111">Te falta un mapa.</span>
            </h1>
            <p className="text-lg md:text-xl text-[#5D3A2E]/80 leading-relaxed max-w-lg" data-id="element-112">
              Una guía práctica para recuperar energía, dormir mejor y dejar de
              vivir inflamada — sin dietas extremas, sin rutinas imposibles.
            </p>
            <div className="pt-4" data-id="element-113">
              <a href="#pricing" className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all bg-[#C4705A] rounded-full hover:bg-[#A65D4A] shadow-lg hover:shadow-xl hover:-translate-y-1" data-id="element-114">
                Descargar la Guía
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-115" />
              </a>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-116">
            <div className="relative z-10 rounded-2xl shadow-2xl overflow-hidden border-4 border-white/50" data-id="element-117">
              <img src="https://cdn.magicpatterns.com/uploads/rCFsGd68BdfSWJFkcCs28i/mockup_-_PERI.png" alt="Perimenopausia Sin Caos Guía Digital en iPad" className="w-full h-auto object-cover" data-id="element-118" />
            </div>
            {/* Decorative elements behind image */}
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#C4705A] rounded-full opacity-20 blur-2xl -z-10" data-id="element-119" />
          </motion.div>
        </div>
      </header>

      {/* 2. OPENING LEAD (Emotional Hook) */}
      <section className="py-20 px-4 bg-[#5D3A2E] text-[#F5EDE6]" data-id="element-120">
        <div className="max-w-3xl mx-auto" data-id="element-121">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="space-y-8 text-lg md:text-xl leading-relaxed font-light" data-id="element-122">
            <p className="font-serif text-2xl md:text-3xl italic text-[#E8D4C9]" data-id="element-123">
              Hay una escena que se repite y casi nadie la dice en voz alta:
            </p>

            <div className="space-y-4 border-l-2 border-[#C4705A] pl-6 py-2" data-id="element-124">
              <p data-id="element-125">Te acuestas cansada.</p>
              <p data-id="element-126">Tu cuerpo está cansado.</p>
              <p className="font-medium text-white" data-id="element-127">Pero tu cabeza… no.</p>
            </div>

            <p data-id="element-128">
              Te despiertas a medianoche o a las 3am. Miras la oscuridad.
              Empiezas a hacer cuentas: lo que comiste, lo que no hiciste, lo
              que deberías estar haciendo.
            </p>

            <p data-id="element-129">
              Y al día siguiente, vuelves a empezar con el mismo plan de
              siempre:{' '}
              <strong className="text-white font-semibold" data-id="element-130">
                más fuerza, menos comida, más café, más aguante.
              </strong>
            </p>

            <p data-id="element-131">
              Y aún así: hinchazón, cansancio, ansiedad, peso que no se mueve.
            </p>

            <div className="pt-8 text-center" data-id="element-132">
              <p className="text-2xl font-serif text-[#E8D4C9]" data-id="element-133">
                Esto no es falta de disciplina. <br data-id="element-134" />
                Es falta de un sistema que entienda la etapa en la que estás.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3. PAIN POINTS */}
      <section className="py-20 px-4 bg-white" data-id="element-135">
        <div className="max-w-4xl mx-auto" data-id="element-136">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="text-center mb-16" data-id="element-137">
            <h2 className="text-3xl md:text-4xl font-bold text-[#5D3A2E] mb-4" data-id="element-138">
              Si esto te suena familiar…
            </h2>
            <div className="w-24 h-1 bg-[#C4705A] mx-auto rounded-full" data-id="element-139" />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-140">
            {['Me levanto cansada aunque dormí.', 'Al final del día parezco embarazada.', 'Me dan antojos que no entiendo.', 'Estoy irritable… y luego me siento culpable.', 'Hago ejercicio y me siento peor.', 'Compro cosas (tés, pastillas) y nada se queda.'].map((pain, index) => <motion.div key={index} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="flex items-start p-6 bg-[#F5EDE6] rounded-xl" data-id="element-141">
                <XCircle className="w-6 h-6 text-[#C4705A] mr-4 flex-shrink-0 mt-1" data-id="element-142" />
                <p className="text-lg text-[#5D3A2E]" data-id="element-143">{pain}</p>
              </motion.div>)}
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="mt-16 text-center bg-[#5D3A2E]/5 p-8 rounded-2xl border border-[#5D3A2E]/10" data-id="element-144">
            <p className="text-lg text-[#5D3A2E]/80 mb-2" data-id="element-145">
              Y lo peor no es el síntoma. Lo peor es esta frase silenciosa:
            </p>
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#C4705A]" data-id="element-146">
              "Antes yo podía… ahora ya no."
            </h3>
          </motion.div>
        </div>
      </section>

      {/* 4. WHY OLD SOLUTIONS FAIL */}
      <section className="py-20 px-4 bg-[#E8D4C9]/30" data-id="element-147">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-148">
          <div className="order-2 md:order-1" data-id="element-149">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} data-id="element-150">
              <h2 className="text-3xl md:text-4xl font-bold text-[#5D3A2E] mb-6" data-id="element-151">
                Porque lo que intentaste no funcionó{' '}
                <span className="text-[#C4705A]" data-id="element-152">(y no fue tu culpa)</span>
              </h2>
              <p className="text-lg text-[#5D3A2E]/80 mb-6 leading-relaxed" data-id="element-153">
                Porque casi todo lo que hay afuera está diseñado para una mujer
                "promedio" que:
              </p>
              <ul className="space-y-3 mb-8" data-id="element-154">
                {['Duerme bien', 'No está con el sistema nervioso en alerta', 'No está lidiando con cambios hormonales', 'Tiene tiempo para cocinar perfecto y entrenar perfecto'].map((item, i) => <li key={i} className="flex items-center text-[#5D3A2E]" data-id="element-155">
                    <div className="w-2 h-2 bg-[#5D3A2E] rounded-full mr-3" data-id="element-156" />
                    {item}
                  </li>)}
              </ul>
              <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-[#C4705A]" data-id="element-157">
                <p className="text-xl font-medium text-[#5D3A2E]" data-id="element-158">
                  Pero tú no necesitas perfección. <br data-id="element-159" />
                  Necesitas{' '}
                  <span className="text-[#C4705A] font-bold" data-id="element-160">ajuste</span>.
                </p>
              </div>
            </motion.div>
          </div>
          <div className="order-1 md:order-2 flex justify-center" data-id="element-161">
            <div className="relative w-full max-w-md aspect-square bg-[#E8D4C9] rounded-full flex items-center justify-center p-8" data-id="element-162">
              <div className="absolute inset-0 border-2 border-dashed border-[#5D3A2E]/20 rounded-full animate-spin-slow" style={{
              animationDuration: '20s'
            }} data-id="element-163" />
              <div className="text-center space-y-4" data-id="element-164">
                <AlertCircle className="w-16 h-16 text-[#C4705A] mx-auto" data-id="element-165" />
                <p className="text-xl font-serif italic text-[#5D3A2E]" data-id="element-166">
                  "La mayoría de planes te dicen qué hacer en general. Este te
                  dice qué hacer <strong className="not-italic" data-id="element-167">hoy</strong>."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. UNIQUE MECHANISM */}
      <section className="py-24 px-4 bg-[#5D3A2E] text-[#F5EDE6] overflow-hidden" data-id="element-168">
        <div className="max-w-6xl mx-auto" data-id="element-169">
          <div className="text-center mb-16" data-id="element-170">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-171">
              El ÚNICO mecanismo del método
            </h2>
            <p className="text-xl text-[#E8D4C9]" data-id="element-172">
              Mapa de Síntomas → 4 Perfiles → Micro‑hábitos por fase
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-173">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-[#C4705A]/30 -z-10" data-id="element-174" />

            {/* Step 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-[#F5EDE6] text-[#5D3A2E] p-8 rounded-2xl relative" data-id="element-175">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-[#C4705A] text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg" data-id="element-176">
                1
              </div>
              <h3 className="mt-6 text-xl font-bold mb-4 text-center" data-id="element-177">
                Mapa de Síntomas
              </h3>
              <p className="text-center text-sm text-[#5D3A2E]/70 mb-4" data-id="element-178">
                (3 minutos al día)
              </p>
              <p className="text-center" data-id="element-179">
                Mides lo mínimo: energía, sueño, antojos, ansiedad, hinchazón.
              </p>
            </motion.div>

            {/* Step 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-[#F5EDE6] text-[#5D3A2E] p-8 rounded-2xl relative" data-id="element-180">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-[#C4705A] text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg" data-id="element-181">
                2
              </div>
              <h3 className="mt-6 text-xl font-bold mb-4 text-center" data-id="element-182">
                4 Perfiles
              </h3>
              <p className="text-center text-sm text-[#5D3A2E]/70 mb-4" data-id="element-183">
                (Elige el dominante)
              </p>
              <ul className="space-y-2 text-sm" data-id="element-184">
                <li className="flex items-center" data-id="element-185">
                  <div className="w-1.5 h-1.5 bg-[#C4705A] rounded-full mr-2" data-id="element-186" />
                  Niebla + Cansancio
                </li>
                <li className="flex items-center" data-id="element-187">
                  <div className="w-1.5 h-1.5 bg-[#C4705A] rounded-full mr-2" data-id="element-188" />
                  Ansiedad + Sueño roto
                </li>
                <li className="flex items-center" data-id="element-189">
                  <div className="w-1.5 h-1.5 bg-[#C4705A] rounded-full mr-2" data-id="element-190" />
                  Antojos + Hambre rara
                </li>
                <li className="flex items-center" data-id="element-191">
                  <div className="w-1.5 h-1.5 bg-[#C4705A] rounded-full mr-2" data-id="element-192" />
                  Inflamación + Retención
                </li>
              </ul>
            </motion.div>

            {/* Step 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.5
          }} className="bg-[#F5EDE6] text-[#5D3A2E] p-8 rounded-2xl relative" data-id="element-193">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-[#C4705A] text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg" data-id="element-194">
                3
              </div>
              <h3 className="mt-6 text-xl font-bold mb-4 text-center" data-id="element-195">
                Micro‑hábitos
              </h3>
              <p className="text-center text-sm text-[#5D3A2E]/70 mb-4" data-id="element-196">
                (Acciones pequeñas)
              </p>
              <p className="text-center" data-id="element-197">
                Sin drama. Sin "mañana empiezo". Sin castigo. El resultado:
                dejas de improvisar.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. WHAT'S INCLUDED */}
      <section className="py-20 px-4 bg-[#F5EDE6]" data-id="element-198">
        <div className="max-w-5xl mx-auto" data-id="element-199">
          <div className="text-center mb-12" data-id="element-200">
            <h2 className="text-3xl md:text-4xl font-bold text-[#5D3A2E]" data-id="element-201">
              Qué incluye "Perimenopausia Sin Caos"
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-202">
            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8D4C9]" data-id="element-203">
              <div className="flex items-center mb-6" data-id="element-204">
                <BookOpen className="w-8 h-8 text-[#C4705A] mr-4" data-id="element-205" />
                <h3 className="text-xl font-bold text-[#5D3A2E]" data-id="element-206">
                  PDF Principal
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-207">
                {['Explicación clara (sin jerga) de qué está pasando', 'Plantilla del Mapa de Síntomas (14 días)', 'Los 4 Perfiles y cómo identificarte', 'Micro‑hábitos por perfil (comida, sueño, estrés)', 'Plan completo de 14 días con estructura diaria'].map((item, i) => <li key={i} className="flex items-start text-[#5D3A2E]/80" data-id="element-208">
                    <CheckCircle2 className="w-5 h-5 text-[#C4705A] mr-3 mt-0.5 flex-shrink-0" data-id="element-209" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8D4C9]" data-id="element-210">
              <div className="flex items-center mb-6" data-id="element-211">
                <ShieldCheck className="w-8 h-8 text-[#C4705A] mr-4" data-id="element-212" />
                <h3 className="text-xl font-bold text-[#5D3A2E]" data-id="element-213">
                  Protocolos Especiales
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-214">
                {['Protocolo de sueño y el plan "3am"', 'Movimiento inteligente 45+ (sin reventarte)', 'Guía de conversación sobre suplementos', 'Checklist para tu médico (preguntas clave)', 'Enfoque educativo, sin promesas raras'].map((item, i) => <li key={i} className="flex items-start text-[#5D3A2E]/80" data-id="element-215">
                    <CheckCircle2 className="w-5 h-5 text-[#C4705A] mr-3 mt-0.5 flex-shrink-0" data-id="element-216" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. WHO IT'S FOR */}
      <section className="py-20 px-4 bg-[#E8D4C9]/20" data-id="element-217">
        <div className="max-w-5xl mx-auto" data-id="element-218">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-219">
            <div className="space-y-6" data-id="element-220">
              <h3 className="text-2xl font-bold text-[#5D3A2E] flex items-center" data-id="element-221">
                <span className="w-8 h-8 bg-[#C4705A] text-white rounded-full flex items-center justify-center mr-3 text-sm" data-id="element-222">
                  ✓
                </span>
                Es para ti si:
              </h3>
              <ul className="space-y-4 pl-11" data-id="element-223">
                {['Tienes 45–60 años', 'Sientes que tu cuerpo cambió "sin permiso"', 'Quieres una estructura simple y medible', 'Estás cansada de gastar en cosas al azar'].map((item, i) => <li key={i} className="text-[#5D3A2E] list-disc" data-id="element-224">
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="space-y-6 opacity-70" data-id="element-225">
              <h3 className="text-2xl font-bold text-[#5D3A2E] flex items-center" data-id="element-226">
                <span className="w-8 h-8 bg-[#5D3A2E] text-white rounded-full flex items-center justify-center mr-3 text-sm" data-id="element-227">
                  ✕
                </span>
                No es para ti si:
              </h3>
              <ul className="space-y-4 pl-11" data-id="element-228">
                {['Buscas una cura milagro', 'Quieres bajar 20 lb en 2 semanas', 'No estás dispuesta a medir 3 minutos al día'].map((item, i) => <li key={i} className="text-[#5D3A2E] list-disc" data-id="element-229">
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 8. WHAT YOU'LL NOTICE FIRST */}
      <section className="py-20 px-4 bg-white" data-id="element-230">
        <div className="max-w-4xl mx-auto text-center" data-id="element-231">
          <h2 className="text-3xl font-bold text-[#5D3A2E] mb-12" data-id="element-232">
            Lo que vas a notar primero
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-233">
            {[{
            icon: Brain,
            text: 'Menos caos mental al elegir qué comer'
          }, {
            icon: Battery,
            text: 'Más estabilidad en energía'
          }, {
            icon: Activity,
            text: 'Menos "inflamación misteriosa"'
          }, {
            icon: Moon,
            text: 'Noches menos interrumpidas'
          }].map((item, i) => <motion.div key={i} whileHover={{
            scale: 1.05
          }} className="p-6 bg-[#F5EDE6] rounded-xl flex flex-col items-center text-center" data-id="element-234">
                <item.icon className="w-10 h-10 text-[#C4705A] mb-4" data-id="element-235" />
                <p className="text-[#5D3A2E] font-medium" data-id="element-236">{item.text}</p>
              </motion.div>)}
          </div>
          <p className="mt-12 text-lg text-[#5D3A2E]/80 italic" data-id="element-237">
            "No te prometo magia. Te prometo señales reales."
          </p>
        </div>
      </section>

      {/* 9. TESTIMONIALS */}
      <TestimonialsSection data-id="element-238" />

      {/* 10. PRICING & CTA */}
      <section id="pricing" className="py-24 px-4 bg-[#5D3A2E] text-[#F5EDE6] relative overflow-hidden" data-id="element-239">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-240">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#C4705A] rounded-full blur-3xl" data-id="element-241" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#C4705A] rounded-full blur-3xl" data-id="element-242" />
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-243">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-244">
            Perimenopausia Sin Caos
          </h2>
          <p className="text-xl text-[#E8D4C9] mb-12" data-id="element-245">
            PDF Completo + Plan de 14 días + Protocolos
          </p>

          <div className="bg-[#F5EDE6] text-[#5D3A2E] p-8 md:p-12 rounded-3xl shadow-2xl max-w-lg mx-auto transform transition-transform hover:scale-[1.02]" data-id="element-246">
            {/* Discount badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C4705A] text-white rounded-full text-sm font-bold mb-6" data-id="element-247">
              <Zap className="w-4 h-4" data-id="element-248" />
              ¡OFERTA ESPECIAL!
            </div>

            <div className="flex justify-center items-baseline mb-2" data-id="element-249">
              <span className="text-6xl font-bold text-[#C4705A]" data-id="element-250">$6.36</span>
            </div>
            <div className="flex justify-center items-center gap-2 mb-8" data-id="element-251">
              <span className="text-xl text-[#5D3A2E]/50 line-through" data-id="element-252">
                $29
              </span>
              <span className="px-2 py-1 bg-[#C4705A]/10 text-[#C4705A] text-sm font-semibold rounded" data-id="element-253">
                -78% OFF
              </span>
            </div>

            <button onClick={handleAddToCart} className="w-full py-4 px-8 bg-[#C4705A] text-white text-xl font-bold rounded-full hover:bg-[#A65D4A] transition-colors shadow-lg hover:shadow-xl mb-6 flex items-center justify-center" data-id="element-254">
              Descárgalo Hoy
              <ArrowRight className="ml-2 w-6 h-6" data-id="element-255" />
            </button>

            <div className="flex items-center justify-center text-sm text-[#5D3A2E]/70 gap-2" data-id="element-256">
              <Download className="w-4 h-4" data-id="element-257" />
              <span data-id="element-258">Entrega digital inmediata a tu email</span>
            </div>
          </div>
        </div>
      </section>

      {/* 11. FINAL CLOSE */}
      <section className="py-20 px-4 bg-[#F5EDE6]" data-id="element-259">
        <div className="max-w-2xl mx-auto text-center space-y-8" data-id="element-260">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#5D3A2E]" data-id="element-261">
            No necesitas más fuerza. <br data-id="element-262" />
            Necesitas <span className="text-[#C4705A]" data-id="element-263">mejor ajuste</span>.
          </h2>
          <p className="text-xl text-[#5D3A2E]/80" data-id="element-264">
            Tu cuerpo no está roto. Solo cambió de reglas. <br data-id="element-265" />Y este PDF es
            tu manual para dejar de pelearte con eso.
          </p>
          <div className="pt-8" data-id="element-266">
            <a href="#hero" className="text-[#C4705A] font-semibold hover:text-[#5D3A2E] transition-colors border-b-2 border-[#C4705A] pb-1" data-id="element-267">
              Volver arriba para empezar
            </a>
          </div>
        </div>
      </section>

      {/* Footer with Privacy and Terms */}
      <footer className="py-12 bg-[#5D3A2E] text-[#E8D4C9]/60 text-center text-sm" data-id="element-268">
        <p data-id="element-269">
          © {new Date().getFullYear()} Perimenopausia Sin Caos. Todos los
          derechos reservados.
        </p>
        <p className="mt-2" data-id="element-270">
          Este producto es informativo y no sustituye consejo médico
          profesional.
        </p>
        <div className="mt-6 flex items-center justify-center gap-6" data-id="element-271">
          <button onClick={() => setPrivacyOpen(true)} className="text-[#E8D4C9]/80 hover:text-[#E8D4C9] transition-colors underline underline-offset-2" data-id="element-272">
            Política de Privacidad
          </button>
          <span className="text-[#E8D4C9]/30" data-id="element-273">|</span>
          <button onClick={() => setTermsOpen(true)} className="text-[#E8D4C9]/80 hover:text-[#E8D4C9] transition-colors underline underline-offset-2" data-id="element-274">
            Términos y Condiciones
          </button>
        </div>
      </footer>
    </div>;
}